import React, { useRef, useState } from "react";
import { Camera, Loader2, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Drop-in picker: file → client-side resize → square JPEG data URL.
 *
 * Props:
 *  - value: current data-URL (or null)
 *  - onChange: (dataUrl|null) => void
 *  - size: rendered avatar diameter px (default 96)
 *  - label: small caption under the circle
 *
 * Keeps payload tiny: resizes longest edge to 384 px @ JPEG q=0.85 ⇒ ~25–60 KB.
 * Cap at 600 KB before resize to guard against giant phone shots crashing the
 * canvas; gracefully toast-fails if exceeded after resize.
 */
const MAX_DIM = 384;
const TARGET_MAX_BYTES = 600 * 1024;

async function fileToResizedDataUrl(file) {
  if (!/^image\//.test(file.type)) throw new Error("Pick an image file (JPG / PNG / WEBP).");
  if (file.size > 12 * 1024 * 1024) throw new Error("Image is too large (12 MB max).");
  const bitmap = await (window.createImageBitmap
    ? createImageBitmap(file)
    : new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = () => reject(new Error("Couldn't read that image."));
        im.src = URL.createObjectURL(file);
      }));
  const { width, height } = bitmap;
  const side = Math.min(width, height);
  // Centre-crop to a square first so the avatar isn't squashed.
  const sx = (width - side) / 2;
  const sy = (height - side) / 2;
  const out = Math.min(MAX_DIM, side);
  const canvas = document.createElement("canvas");
  canvas.width = out;
  canvas.height = out;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(bitmap, sx, sy, side, side, 0, 0, out, out);
  // Successive quality reductions until under the byte cap.
  let q = 0.85;
  let dataUrl = canvas.toDataURL("image/jpeg", q);
  while (dataUrl.length > TARGET_MAX_BYTES && q > 0.4) {
    q -= 0.1;
    dataUrl = canvas.toDataURL("image/jpeg", q);
  }
  if (dataUrl.length > TARGET_MAX_BYTES) throw new Error("Image is still too big after compression — try a smaller one.");
  return dataUrl;
}

export default function AvatarPicker({ value, onChange, size = 96, label = "Profile picture" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = ""; // allow re-picking the same file
    if (!f) return;
    setBusy(true);
    try {
      const url = await fileToResizedDataUrl(f);
      onChange(url);
    } catch (err) {
      toast.error(err.message || "Couldn't process that image.");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = () => onChange(null);

  return (
    <div className="flex items-center gap-4" data-testid="avatar-picker">
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        className="relative shrink-0 rounded-full overflow-hidden border-2 border-[#ff4500] bg-[#fff4ee] hover:opacity-90 transition group"
        style={{ width: size, height: size }}
        aria-label="Choose a profile picture"
        data-testid="avatar-picker-button"
      >
        {value ? (
          <img src={value} alt="" className="w-full h-full object-cover" />
        ) : (
          <Camera className="absolute inset-0 m-auto w-7 h-7 text-[#ff4500]" strokeWidth={1.75} />
        )}
        {busy && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-[#ff4500]" />
          </div>
        )}
        <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[10px] font-bold uppercase tracking-wider py-0.5 opacity-0 group-hover:opacity-100 transition">
          {value ? "Change" : "Upload"}
        </span>
      </button>
      <div className="text-sm">
        <div className="eyebrow mb-1">{label}</div>
        <div className="text-xs text-[#666]">
          {value ? "Looking sharp. Click the circle to change." : "Optional · JPG / PNG / WEBP · we'll resize it for you."}
        </div>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="mt-2 inline-flex items-center gap-1 text-xs text-[#ff4500] underline"
            data-testid="avatar-picker-clear"
          >
            <X className="w-3 h-3" strokeWidth={2} /> Remove
          </button>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleFile}
        data-testid="avatar-picker-input"
      />
    </div>
  );
}
